import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';

const InquiryForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    tour_type: 'domestic',
    service_type: '',
    destination: '',
    message: '',
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [destinationsLoading, setDestinationsLoading] = useState(false);

  const categoryMap = {
    domestic: 'Domestic',
    international: 'International',
    religious: 'Religious',
    wildlife: 'Wildlife',
  };

  const fetchDestinations = useCallback(async (tourType) => {
    const category = categoryMap[tourType];
    if (!category) return;

    setDestinationsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/tours?category=${encodeURIComponent(category)}&limit=100`
      );
      const result = await response.json();
      const tours = result.tours || [];
      // Extract unique destination titles
      const uniqueDestinations = [...new Set(tours.map((t) => t.title))];
      setDestinations(uniqueDestinations);
    } catch (err) {
      console.error('Failed to fetch destinations:', err);
      setDestinations([]);
    } finally {
      setDestinationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDestinations(formData.tour_type);
  }, [formData.tour_type, fetchDestinations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to submit inquiry. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const radioOptions = [
    { label: 'Domestic', value: 'domestic' },
    { label: 'International', value: 'international' },
    { label: 'Religious', value: 'religious' },
    { label: 'Wildlife', value: 'wildlife' },
  ];

  const serviceTypeOptions = [
    { label: 'Tour Package', value: 'tour' },
    { label: 'Flight Booking', value: 'flight' },
    { label: 'Hotel Booking', value: 'hotel' },
    { label: 'Railway Booking', value: 'railway' },
    { label: 'Visa Assistance', value: 'visa' },
    { label: 'Honeymoon Package', value: 'honeymoon' },
    { label: 'Corporate Booking', value: 'corporate' },
  ];

  return (
    <>
      {success ? (
        <Box sx={{
          textAlign: 'center',
          py: 4,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f4f8 100%)',
          borderRadius: 2,
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0C2340', mb: 1 }}>
            Thank You!
          </Typography>
          <Typography color="text.secondary">
            We've received your inquiry and will get back to you within 24 hours.
          </Typography>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            background: '#fff',
            borderRadius: { xs: 2.5, sm: 3 },
            p: { xs: 1, sm: 3 },
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: '#0C2340', mb: 0.25, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
          >
            Get in Touch
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: { xs: 0.25, sm: 1.5 }, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            Fill out the form and our travel experts will reach out to you.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: { xs: 0.5, sm: 1 }, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{
            mb: { xs: 0.5, sm: 1.5 },
            background: alpha('#D4AF37', 0.05),
            borderRadius: 2,
            p: { xs: 0.5, sm: 1.5 }
          }}>
            <FormLabel sx={{ fontWeight: 600, color: '#0C2340', mb: { xs: 0.1, sm: 1 }, display: 'block', fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
              Tour Type
            </FormLabel>
            <RadioGroup
              row
              name="tour_type"
              value={formData.tour_type}
              onChange={handleChange}
              sx={{
                flexWrap: 'wrap',
                gap: { xs: 0, sm: 0.5 },
                '& .MuiFormControlLabel-root': {
                  marginRight: { xs: 0, sm: 0 },
                  marginLeft: { xs: -0.5, sm: 0 },
                  marginBottom: { xs: -0.25, sm: 0 },
                },
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                },
                '& .MuiRadio-root': {
                  padding: { xs: '4px', sm: '9px' },
                },
              }}
            >
              {radioOptions.map((type) => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={
                    <Radio
                      sx={{
                        color: '#ced4da',
                        '&.Mui-checked': { color: '#D4AF37' },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500, color: '#0C2340' }}>
                      {type.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Box>

          <Grid container spacing={{ xs: 0.75, sm: 1.5 }}>
            <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Service Type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4AF37',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      minHeight: { xs: '24px', sm: 'auto' },
                    },
                  }}
                >
                  {serviceTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4AF37',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      minHeight: { xs: '24px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4AF37',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      minHeight: { xs: '24px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4AF37',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      minHeight: { xs: '24px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Destination"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  required
                  disabled={destinationsLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4AF37',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      minHeight: { xs: '24px', sm: 'auto' },
                    },
                  }}
                >
                {destinationsLoading ? (
                  <MenuItem value="" disabled>
                    Loading destinations...
                  </MenuItem>
                ) : destinations.length === 0 ? (
                  <MenuItem value="" disabled>
                    No destinations available
                  </MenuItem>
                ) : (
                  destinations.map((dest) => (
                    <MenuItem key={dest} value={dest}>
                      {dest}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={1}
                minRows={1}
                maxRows={2}
                label="Your Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&.Mui-focused fieldset': {
                      borderColor: '#D4AF37',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    minHeight: { xs: '24px', sm: 'auto' },
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 0.25, sm: 1 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              By submitting, you agree to our{' '}
              <Box
                component="span"
                sx={{ color: '#D4AF37', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Box>
            </Typography>
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              mt: { xs: 0.5, sm: 1.5 },
              py: { xs: 0.6, sm: 1.5 },
              borderRadius: '24px',
              fontWeight: 700,
              fontSize: '1rem',
              bgcolor: '#0C2340',
              color: 'white',
              '&:hover': {
                bgcolor: '#E4C65A',
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: alpha('#D4AF37', 0.4),
                color: alpha('#0C2340', 0.6),
              },
            }}
            endIcon={loading ? <CircularProgress size={20} sx={{ color: '#0C2340' }} /> : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </Box>
      )}
    </>
  );
};

export default InquiryForm;